import React, { createContext, HTMLAttributes, useState, useRef, useEffect, useCallback } from 'react'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { Media } from '../media'
import { Row, Col } from '../../layout'
import { PhotoGalleryActionBar } from './PhotoGalleryActionBar'
import { createPortal } from 'react-dom'
import { NormalizedMedia } from '@evertel/media'
import { useUI } from '../../UIContext'
import { useService } from '@evertel/di'
import { Modal, ModalBody, ModalHeader, Tooltip, TooltipContent, TooltipTrigger } from '../..'
import { UserController } from '@evertel/blue-user'
import { APIDataBlueUser } from '@evertel/types'
import { MediaController } from '@evertel/web/media'

interface PhotoGalleryProps extends HTMLAttributes<HTMLDivElement> {
    visible?: boolean
}

interface ContextProps extends PhotoGalleryProps {
    setSelectedIndex: (number) => void
    setVisible: React.Dispatch<React.SetStateAction<boolean>>
    setMedia: (media: NormalizedMedia[], selectedMedia?: number) => void
    media?: NormalizedMedia[]
    selectedUserController?: UserController
}

export const PhotoGalleryContext = createContext({} as ContextProps)

const PhotoGallery: React.FC<PhotoGalleryProps> = observer(({
    children
}) => {

    const modalRef = useRef<HTMLDivElement>(null)
    const previouslyFocusedElement = useRef<HTMLElement | null>(null)

    const mediaRef = useRef(null)
    const galleryIndexRef = useRef(null)

    const { addToast } = useUI()

    const userController = useService(UserController, [])
    const mediaController = useService(MediaController, [])

    const [visible, setVisible] = useState(false)
    const [media, _setMedia] = useState<NormalizedMedia[]>([])
    const [selectedIndex, setSelectedIndex] = useState(0)
    const [selectedUserController, setSelectedUserController] = useState<UserController>()
    const [scale, setScale] = useState(1)

    const [windowSize, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight
    })

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        switch (event.key) {
            case '0':
                resetZoom()
                break
            case '+':
            case '=':
                zoomIn()
                break
            case '-':
            case '_':
                zoomOut()
                break
            case 'ArrowLeft':
                switchSelected('prev')
                break
            case 'ArrowRight':
                switchSelected('next')
                break
        }
    }

    //adjusts css transform:scale for zoom changes
    useEffect(() => {
        if (mediaRef.current) {
            if (mediaRef.current.getInternalPlayer) {
                mediaRef.current.getInternalPlayer().style.transform = `scale(${scale})`
            } else {
                mediaRef.current.style.transform = `scale(${scale})`
            }
        }
    }, [scale])

    //resets zoom on open/close
    useEffect(() => {
        resetZoom()
    }, [visible])

    // Triggers redraw on resize
    useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight
            })
        }

        window.addEventListener('resize', handleResize)

        return () => {
            window.removeEventListener('resize', handleResize)
        }
    }, [])

    useEffect(() => {
        if (!visible) {
            setSelectedUserController(null)
            setSelectedIndex(0)
        }

        const selectedUserId = media?.[selectedIndex]?.ownerId

        if (selectedUserId) {
            userController.init(selectedUserId)
            setSelectedUserController(userController)
        } else {
            userController.init(-1)
            setSelectedUserController(null)
        }
    }, [selectedIndex, media, visible, userController])

    /**
     * This will only display images and video if those are in the list of provided media.
     * @param media
     * @param mediaId
     */
    const setMedia = (media: NormalizedMedia[], mediaId?: number) => {
        const ivMedia = media.filter(
            (m) => m.mediaType === 'image' || m.mediaType === 'video' || m.mediaType === 'audio'
        )
        const usefulMedia = (ivMedia.length) ? ivMedia : media

        _setMedia(usefulMedia)

        const newIndex = mediaId ? usefulMedia.findIndex((m) => m.id === mediaId) : 0
        setSelectedIndex(newIndex !== -1 ? newIndex : 0)
    }

    const contextValues = {
        media,
        setMedia,
        visible,
        setVisible,
        setSelectedIndex,
        setSelectedUser: setSelectedUserController
    }

    const zoomIn = useCallback((dD = 0.2) => {
        setScale(prevScale => {
            let delta = 0
            if (prevScale < 2.6) {
                delta = dD
            }
            return prevScale + delta
        })
    }, [])

    const zoomOut = useCallback((dD = -0.2) => {
        setScale(prevScale => {
            let delta = 0
            if (prevScale > 0.4) {
                delta = dD
            }
            return prevScale + delta
        })
    }, [])

    const handleWheel = useCallback((event: WheelEvent) => {
        const target = event.target as HTMLElement | null

        // 1. Let the thumbnail scroller behave normally
        if (target && target.closest('.photo-gallery-thumb-wrapper')) {
            return
        }

        const { deltaX, deltaY } = event

        // 2. If the gesture is more horizontal than vertical,
        //    let it pass through (back/forward, horizontal scroll, etc.)
        if (Math.abs(deltaX) > Math.abs(deltaY)) {
            return
        }

        // 3. Vertical-dominant = zoom
        if (deltaY === 0) return

        event.preventDefault()

        if (deltaY < 0) {
            zoomIn(0.03)
        } else {
            zoomOut(-0.03)
        }
    }, [zoomIn, zoomOut])


    const handleShow = useCallback(() => {
        const el = modalRef.current

        if (el) {
            // el.focus()
            el.addEventListener('wheel', handleWheel)
        }
    }, [handleWheel])

    // called by Modal's onCloseDone
    const handleCloseDone = useCallback(() => {
        const el = modalRef.current
        if (el) {
            el.removeEventListener('wheel', handleWheel)
        }
    }, [handleWheel])

    const resetZoom = () => {
        setScale(1)
    }

    const download = async () => {
        const file = media[selectedIndex]

        try {
            await mediaController.downloadMedia(file)
        } catch (error) {
            addToast({
                title: 'Error',
                color: 'danger',
                message: error.message || error
            })
        }
    }

    const switchSelected = (direction: 'next' | 'prev' | 'specific', mediaId?: number) => {
        if (media.length <= 1) return //no switching if there's only 1 item

        const currentIndex = selectedIndex

        let newIndex
        if (direction === 'next') {
            newIndex = (currentIndex + 1) % media.length
        } else if (direction === 'prev') {
            newIndex = (currentIndex - 1 + media.length) % media.length
        } else if (direction === 'specific' && mediaId !== undefined) {
            newIndex = media.findIndex((m) => m.id === mediaId)
        }

        if (newIndex !== undefined) {
            setSelectedIndex(newIndex)
            const buttons = galleryIndexRef.current.querySelectorAll('button')
            buttons[newIndex].focus()
        }
        resetZoom()
    }

    const selected = media[selectedIndex]

    const multipleMedia = media?.length > 1

    return (
        <PhotoGalleryContext.Provider value={contextValues}>
            <Modal
                data-bs-theme="dark"
                ref={modalRef}
                visible={visible}
                onShow={handleShow}
                onCloseDone={handleCloseDone}
                onClose={() => setVisible(false)}
                size="xl"
                fullscreen="lg"
                scrollable
                className="photo-gallery-modal"
                backdrop={true}
                keyboard={true}
                onKeyDown={handleKeyDown}
                tabIndex={-1}
            >
                <ModalHeader style={{alignItems: 'normal'}}>
                    <PhotoGalleryActionBar
                        style={{marginTop: '-0.5rem'}}
                        userController={selectedUserController}
                        media={selected}
                        onZoomIn={() => zoomIn()}
                        onZoomOut={() => zoomOut()}
                        onReset={resetZoom}
                        onDownload={download}
                    />
                </ModalHeader>
                <ModalBody className="photo-gallery-modal-body p-0 overflow-hidden" >
                    <Col
                        valign={selected?.mediaType === 'video' ? 'center' : 'center'}
                        align="center"
                        className="h-100"
                    >
                        <Row
                            align="center"
                            valign="center"
                            style={{
                                position: 'relative',
                                height: selected?.mediaType === 'video' ? 'calc(100% - 125px)' : '100%',
                                width: '95%'
                            }}
                        >
                            {(selected) &&
                                <Media
                                    // this key is essential or it won't change when selected is changed
                                    key={selected.id}
                                    ref={mediaRef}
                                    media={selected as NormalizedMedia}
                                    width={window.innerWidth * 0.7}
                                    height={window.innerHeight * (multipleMedia ? 0.65 : 1 )}
                                    {...(selected.mediaType === 'video' && { maxHeight: '100%' })}
                                    imageProps={{ resizeMode: 'contain' }}
                                    className={`bg-transparent ${selected.mediaType === 'video' ? 'h-100' : ''}`}
                                    style={{
                                        textAlign: 'center',
                                        marginBottom: multipleMedia ? 90 : 0,
                                        maxWidth: '100%'
                                    }}
                                />
                            }
                        </Row>
                        {(multipleMedia) &&
                            <div className="photo-gallery-thumb-wrapper" >
                                <Row
                                    valign="bottom"
                                    className="photo-gallery-thumbs"
                                    ref={galleryIndexRef}
                                >
                                    {media.map((m, index) =>
                                        <div key={m.id}>
                                            <Tooltip>
                                                <TooltipTrigger asChild={true}>
                                                    <button
                                                        className={`p-0 mx-2 ${index === selectedIndex ? 'selected' : ''}`}
                                                        onClick={() => switchSelected('specific', m.id as number)}
                                                    >
                                                        <Media
                                                            key={m.id}
                                                            media={m}
                                                            width={100}
                                                            height={75}
                                                            rounded={true}
                                                            imageProps={{ resizeMode: 'cover', flexibility: 'fixed' }}
                                                            videoProps={{ asThumbnail: true }}
                                                            audioProps={{ height: 75, width: 75, variant: 'icon', fileDuration: m.duration }}
                                                            className={`p-0`}
                                                        />
                                                    </button>
                                                </TooltipTrigger>
                                                <TooltipContent>{m?.fileName}</TooltipContent>
                                            </Tooltip>
                                        </div>
                                    )}
                                </Row>
                            </div>
                        }
                    </Col>
                </ModalBody>
            </Modal>
            {children}
        </PhotoGalleryContext.Provider>
    )
})

export { PhotoGallery }
